import { z } from 'zod';

/** What a workspace calls each level of the hierarchy. Stored in `workspace.settings.terminology`. */
export interface TermPair {
  singular: string;
  plural: string;
}
export interface Terminology {
  space: TermPair;
  folder: TermPair;
  list: TermPair;
}
export type TermKind = keyof Terminology;

const termPairSchema = z.object({
  singular: z.string().trim().min(1).max(32),
  plural: z.string().trim().min(1).max(32),
});
export const terminologySchema = z.object({
  space: termPairSchema,
  folder: termPairSchema,
  list: termPairSchema,
});

export const DEFAULT_TERMS: Terminology = {
  space: { singular: 'Space', plural: 'Spaces' },
  folder: { singular: 'Folder', plural: 'Folders' },
  list: { singular: 'List', plural: 'Lists' },
};

export interface BusinessType {
  id: string;
  label: string;
  summary: string;
  terms: Terminology;
}

/** Picked during onboarding. Each one suggests terminology, which stays editable in Settings. */
export const BUSINESS_TYPES: readonly BusinessType[] = [
  {
    id: 'agency',
    label: 'Marketing or creative agency',
    summary: 'Clients with ongoing services like SEO, design and web.',
    terms: {
      space: { singular: 'Group', plural: 'Groups' },
      folder: { singular: 'Client', plural: 'Clients' },
      list: { singular: 'Service', plural: 'Services' },
    },
  },
  {
    id: 'medical',
    label: 'Medical or healthcare',
    summary: 'Practices, clinics and care teams.',
    terms: {
      space: { singular: 'Department', plural: 'Departments' },
      folder: { singular: 'Program', plural: 'Programs' },
      list: { singular: 'Worklist', plural: 'Worklists' },
    },
  },
  {
    id: 'legal',
    label: 'Law or accounting firm',
    summary: 'Matters and engagements for clients.',
    terms: {
      space: { singular: 'Practice area', plural: 'Practice areas' },
      folder: { singular: 'Client', plural: 'Clients' },
      list: { singular: 'Matter', plural: 'Matters' },
    },
  },
  {
    id: 'consulting',
    label: 'Consulting or professional services',
    summary: 'Engagements delivered to clients.',
    terms: {
      space: { singular: 'Practice', plural: 'Practices' },
      folder: { singular: 'Client', plural: 'Clients' },
      list: { singular: 'Engagement', plural: 'Engagements' },
    },
  },
  {
    id: 'construction',
    label: 'Construction or trades',
    summary: 'Job sites and phases.',
    terms: {
      space: { singular: 'Division', plural: 'Divisions' },
      folder: { singular: 'Project', plural: 'Projects' },
      list: { singular: 'Phase', plural: 'Phases' },
    },
  },
  {
    id: 'software',
    label: 'Software or product team',
    summary: 'Teams shipping projects in sprints.',
    terms: {
      space: { singular: 'Team', plural: 'Teams' },
      folder: { singular: 'Project', plural: 'Projects' },
      list: { singular: 'Sprint', plural: 'Sprints' },
    },
  },
  {
    id: 'realestate',
    label: 'Real estate or property',
    summary: 'Offices, properties and checklists.',
    terms: {
      space: { singular: 'Office', plural: 'Offices' },
      folder: { singular: 'Property', plural: 'Properties' },
      list: { singular: 'Checklist', plural: 'Checklists' },
    },
  },
  {
    id: 'other',
    label: 'Something else',
    summary: 'Start with the standard names and adjust later.',
    terms: DEFAULT_TERMS,
  },
];

export function businessType(id: unknown): BusinessType | undefined {
  return BUSINESS_TYPES.find((b) => b.id === id);
}

/** The terminology for a workspace, falling back to the defaults for anything missing or invalid. */
export function termsFor(settings: Record<string, unknown> | undefined): Terminology {
  const parsed = terminologySchema.safeParse(settings?.terminology);
  return parsed.success ? parsed.data : DEFAULT_TERMS;
}

export function lower(term: string): string {
  return term.charAt(0).toLowerCase() + term.slice(1);
}
